import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Copy, CreditCard } from 'lucide-react';
import { AdvanceBatchDetail } from '@/types';
import { formatDateByDayjs, formatWithThousandSeparator } from '@/utils';
import { disbursementChannelLabel } from './format';

/**
 * Disbursement proof card (DESIGN §4 item 3) — full card once `disbursed_at`
 * exists; before that a slim inline strip ("Awaiting FFC decision" /
 * "Approved — cash-out in progress"), never an empty card shell. Terminal
 * rejected/failed batches render nothing here (the stepper tells the story).
 */
function DisbursementCard({ batch }: { batch: AdvanceBatchDetail }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  if (!batch.disbursed_at) {
    if (batch.status === 'rejected' || batch.status === 'failed' || batch.status === 'closed') {
      return null;
    }
    const stripKey =
      batch.status === 'approved' || batch.status === 'partially_approved'
        ? 'advance_cod_cashout_in_progress'
        : 'advance_cod_awaiting_decision_strip';
    return (
      <div
        className='rounded-xl bg-[rgb(245,245,243)] px-4 py-3 text-[13px] text-[rgb(115,123,140)]'
        data-testid='disbursement-strip'
      >
        {String(t(stripKey))}
      </div>
    );
  }

  const handleCopyReference = () => {
    if (batch.disbursement_reference) {
      navigator.clipboard.writeText(batch.disbursement_reference).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  };

  return (
    <section
      className='rounded-2xl border border-solid border-[rgb(232,230,227)] bg-white p-5'
      data-testid='disbursement-card'
    >
      <div className='mb-4 flex items-center gap-2'>
        <span className='text-[rgb(115,123,140)]'>
          <CreditCard className='h-4 w-4' />
        </span>
        <h2 className='m-0 text-sm font-bold uppercase tracking-wider text-[rgb(57,57,60)]'>
          {String(t('advance_cod_disbursement'))}
        </h2>
      </div>
      <div className='divide-y divide-[rgb(232,230,227)]'>
        <div className='flex items-start justify-between py-2'>
          <span className='text-sm text-[rgb(115,123,140)]'>{String(t('advance_cod_channel'))}</span>
          {/* Human channel label (QA defect 8) — brand names stay Latin in AR */}
          <span className='text-sm font-medium text-[rgb(57,57,60)]'>
            <bdi>
              {disbursementChannelLabel(
                t as (key: string) => string,
                batch.disbursement_channel,
              ) ?? '—'}
            </bdi>
          </span>
        </div>
        <div className='flex items-start justify-between py-2'>
          <span className='text-sm text-[rgb(115,123,140)]'>
            {String(t('advance_cod_reference'))}
          </span>
          <span className='flex items-center gap-1.5'>
            <span className='font-mono text-sm font-medium text-[rgb(57,57,60)]'>
              <bdi>{batch.disbursement_reference ?? '—'}</bdi>
            </span>
            {batch.disbursement_reference ? (
              <button
                type='button'
                onClick={handleCopyReference}
                className='cursor-pointer rounded-md border-0 bg-transparent p-1 transition-colors hover:bg-[rgb(245,245,243)]'
                title={copied ? String(t('copied')) : String(t('copy'))}
              >
                {copied ? (
                  <Check className='h-3.5 w-3.5 text-[rgb(49,196,127)]' />
                ) : (
                  <Copy className='h-3.5 w-3.5 text-[rgb(115,123,140)]' />
                )}
              </button>
            ) : null}
          </span>
        </div>
        <div className='flex items-start justify-between py-2'>
          <span className='text-sm text-[rgb(115,123,140)]'>
            {String(t('advance_cod_disbursed_at'))}
          </span>
          <span className='text-sm font-medium text-[rgb(57,57,60)]'>
            <bdi>{formatDateByDayjs(batch.disbursed_at, 'D MMM YYYY, HH:mm')}</bdi>
          </span>
        </div>
        <div className='flex items-start justify-between py-2'>
          <span className='text-sm text-[rgb(115,123,140)]'>
            {String(t('advance_cod_disbursed_amount'))}
          </span>
          <span className='text-sm font-bold text-[rgb(57,57,60)]'>
            <bdi>
              {formatWithThousandSeparator(Number(batch.disbursed_amount ?? 0))} {String(t('iqd'))}
            </bdi>
          </span>
        </div>
      </div>
    </section>
  );
}

export default DisbursementCard;
